import type {
	ColumnFilter,
	ColumnSort,
	PaginationState,
	RowSelectionState,
	TableOptions,
	TableState,
	Updater,
} from "@tanstack/vue-table";
import { isEqual } from "lodash-es";
import type { SimplifyDeep } from "type-fest";
import { reactive, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

export type StorageLocation = "memory" | "url";

export type ManagedState = Extract<
	keyof TableState,
	"globalFilter" | "columnFilters" | "pagination" | "sorting" | "rowSelection"
>;

type UpdaterHandlers = `on${Capitalize<ManagedState>}Change`;

export interface DataCollectionState {
	filter: string;
	fieldFilters: ColumnFilter[];
	pagination: PaginationState;
	sorting: ColumnSort[];
	selection: RowSelectionState;
}

export type DataCollectionStateOptions = SimplifyDeep<
	Required<Pick<TableOptions<any>, UpdaterHandlers>> & {
		state: Pick<TableState, ManagedState>;
	}
>;

const defaultPageIndex = 0;
const defaultPageSize = 20;

function memoryState(): DataCollectionState {
	return reactive({
		filter: "",
		fieldFilters: [],
		pagination: { pageIndex: defaultPageIndex, pageSize: defaultPageSize },
		sorting: [],
		selection: {},
	});
}

type StateQueryParams = {
	filter?: string;
	pageIndex?: string;
	pageSize?: string;
} & Partial<Record<`col.${string}`, string[]>> &
	Partial<Record<`sort.${string}`, "asc" | "desc">>;

function urlQueryParamState() {
	const state = memoryState();

	const route = useRoute();
	const router = useRouter();

	function loadParam() {
		const query = route.query as StateQueryParams;

		for (const key in query) {
			if (key === "filter") {
				state.filter = query.filter ?? "";
				continue;
			}

			if (key === "pageIndex") {
				state.pagination.pageIndex = Number(query.pageIndex);
			}

			if (key === "pageSize") {
				state.pagination.pageSize = Number(query.pageSize);
			}

			if (key.startsWith("col.")) {
				const [_, col] = key.split(".");

				const value = query[key];

				state.fieldFilters.push({
					id: col,
					value: Array.isArray(value) ? value : [value],
				});
			}

			if (key.startsWith("sort.")) {
				const [_, col] = key.split(".");
				state.sorting.push({
					id: col,
					desc: query[key] === "desc",
				});
			}
		}
	}

	loadParam();

	function saveParams() {
		const query: StateQueryParams = { ...route.query } as any;

		const setOrDeleteParam = <T>(
			key: keyof StateQueryParams,
			value: T | undefined,
			defaultValue: T
		) => {
			if (value !== defaultValue && value !== undefined && value !== "") {
				query[key] = String(value) as any;
			} else {
				delete query[key];
			}
		};

		setOrDeleteParam("filter", state.filter, "");
		setOrDeleteParam("pageIndex", state.pagination.pageIndex, defaultPageIndex);
		setOrDeleteParam("pageSize", state.pagination.pageSize, defaultPageSize);

		for (const key in query) {
			if (key.startsWith("col.")) {
				delete query[key];
			}
		}

		for (const filter of state.fieldFilters) {
			const key = `col.${filter.id}` as const;

			query[key] = filter.value as string[];
		}

		for (const key in query) {
			if (key.startsWith("sort.")) {
				delete query[key];
			}
		}

		for (const sort of state.sorting) {
			const key = `sort.${sort.id}` as const;

			query[key] = sort.desc ? "desc" : "asc";
		}

		if (isEqual(route.query, query)) {
			return;
		}

		const { params, hash } = route;

		router.replace({
			params,
			query,
			hash,
		});
	}

	const prevState: Record<string, any> = {};
	watch(
		state,
		(state) => {
			if (isEqual(state, prevState)) {
				return;
			}

			Object.assign(prevState, state);

			saveParams();
		},
		{ deep: true, flush: "post" }
	);

	return state;
}

export function useDataCollectionState(
	storage: StorageLocation
): [DataCollectionState, DataCollectionStateOptions] {
	let state: DataCollectionState;
	if (storage === "url") {
		state = urlQueryParamState();
	} else {
		state = memoryState();
	}

	function makeUpdater<T extends keyof DataCollectionState>(name: T) {
		return (updater: Updater<DataCollectionState[T]>) => {
			state[name] = updater instanceof Function ? updater(state[name]) : updater;
		};
	}

	return [
		state,
		{
			state: {
				get columnFilters() {
					return state.fieldFilters;
				},
				get globalFilter() {
					return state.filter;
				},
				get pagination() {
					return state.pagination;
				},
				get rowSelection() {
					return state.selection;
				},
				get sorting() {
					return state.sorting;
				},
			},
			onGlobalFilterChange: makeUpdater("filter"),
			onColumnFiltersChange: makeUpdater("fieldFilters"),
			onPaginationChange: makeUpdater("pagination"),
			onRowSelectionChange: makeUpdater("selection"),
			onSortingChange: makeUpdater("sorting"),
		},
	];
}
